import * as React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Space, Table, Tag } from "antd";
import { setOrderReturnedToChainz } from "../store/actions/imsAdminActions";
import moment from "moment";
import {
  antTableWrapperSx,
  defaultPaginatedTableProps,
  idTableColumn,
} from "./AntTableStyles";
import { getOrderCourierLabel, getOrderTrackingId } from "../utils/shippingDisplay";

export const OrdersTable = ({
  token,
  orders,
  isLoading,
  pagination,
  page,
  setPage,
  setOrderReturnedToChainz,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const role = token?.split(" ")[0];

  const columns = [
    idTableColumn(),
    {
      title: "Shopify Number",
      dataIndex: "shopifyOrderNumber",
    },
    {
      title: "Tracking ID",
      dataIndex: "trackingId",
    },
    {
      title: "Courier",
      dataIndex: "courier",
    },
    {
      title: "Date Ordered",
      dataIndex: "date",
      sorter: (a, b) => {
        return moment(a.date).isBefore(b.date) ? -1 : 1;
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Date Fulfilled",
      dataIndex: "dateFulfilled",
      sorter: (a, b) => {
        return moment(a.dateFulfilled).isBefore(b.dateFulfilled) ? -1 : 1;
      },
    },
    {
      title: "Date Delivered",
      dataIndex: "dateDelivered",
      sorter: (a, b) => {
        return moment(a.dateDelivered).isBefore(b.dateDelivered) ? -1 : 1;
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customer",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, { status }) => (
        <Tag
          color={
            status === "Delivered"
              ? "green"
              : status === "Cancelled"
                ? "red"
                : status === "Prepared"
                  ? "blue"
                  : "yellow"
          }
        >
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "chainzFinanceStatus",
      render: (_, { chainzFinanceStatus }) => (
        <Tag color={chainzFinanceStatus === "Paid" ? "green" : "yellow"}>
          {chainzFinanceStatus.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
    },
    {
      title: "Actions",
      render: (_, { _id, status }) => (
        <Space>
          <Button
            onClick={() =>
              navigate(
                `${role === "InventoryAdmin" ? "/ims/admin" : "/ims/warehouse-staff"}/order/${_id}`
              )
            }
          >
            View Order
          </Button>
          {status === "Returned" && (
            <Button onClick={() => setOrderReturnedToChainz({ orderId: _id })}>
              Set as Returned to Chainz
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const data = orders?.map((row) => {
    return {
      _id: row._id,
      trackingId: getOrderTrackingId(row),
      courier: getOrderCourierLabel(row, row.vendor),
      shopifyOrderNumber: row.shopifyOrderNumber,
      chainzFinanceStatus: row.chainzFinanceStatus,
      date: moment(row.createdAt).format("lll"),
      dateFulfilled: row.preparedAt ? moment(row.preparedAt).format("lll") : null,
      dateDelivered: row.deliveredAt ? moment(row.deliveredAt).format("lll") : null,
      status: row.status,
      customer: row.orderFulfillment?.customer,
      vendor: row.vendor.name,
    };
  });

  return (
    <Box sx={antTableWrapperSx(isMobile)}>
      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        showSorterTooltip={{
          target: "sorter-icon",
        }}
        pagination={defaultPaginatedTableProps({
          current: page,
          pageSize: parseInt(pagination?.limit) || 100,
          total: pagination?.totalDocs,
          onChange: (nextPage) => setPage(nextPage),
        })}
        scroll={{ x: isMobile ? 300 : 1200 }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  token: state?.auth?.token,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { setOrderReturnedToChainz };

export default connect(mapStateToProps, mapDispatchToProps)(OrdersTable);
